import { Anchor, Badge, Group, Stack, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { WEEKLY_OVERVIEW_WEEKS, type WeeklyOverviewRow } from "@msc/shared";

import { formatBerlinDate } from "../../lib/date.js";
import { kwLabel } from "../../lib/weekly.js";

/** Ab so vielen Wochen ohne Weekly wird die Zahl hervorgehoben. */
const LUECKEN_HERVORHEBEN_AB = 2;

export interface WeeklyPaar {
  employeeId: string;
  segmentId: string;
}

/**
 * Übersicht aller sichtbaren Lead × Segment-Paare für die Führungskraft —
 * read-only, Muster `BudgetSection`. Navigation über `onOpen`, damit die
 * Komponente ohne Router testbar bleibt; der Name ist zusätzlich ein echter Link.
 */
export function WeeklyOverview({
  rows,
  onOpen,
}: {
  rows: WeeklyOverviewRow[];
  onOpen: (paar: WeeklyPaar) => void;
}) {
  const { t } = useTranslation();

  if (rows.length === 0) return <Text c="dimmed">{t("weekly.noLeads")}</Text>;

  return (
    <Table.ScrollContainer minWidth={720}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("weekly.overview.lead")}</Table.Th>
            <Table.Th>{t("weekly.overview.segment")}</Table.Th>
            <Table.Th>{t("weekly.overview.lastWeekly")}</Table.Th>
            <Table.Th>{t("weekly.overview.openTodos")}</Table.Th>
            <Table.Th ta="right">
              {t("weekly.overview.missingWeeks")}
              <Text size="xs" c="dimmed" fw={400}>
                {t("weekly.overview.missingWeeksHint", { n: WEEKLY_OVERVIEW_WEEKS })}
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <OverviewZeile key={`${r.employeeId}:${r.segmentId}`} row={r} onOpen={onOpen} />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function OverviewZeile({ row, onOpen }: { row: WeeklyOverviewRow; onOpen: (paar: WeeklyPaar) => void }) {
  const { t } = useTranslation();
  const paar = { employeeId: row.employeeId, segmentId: row.segmentId };
  const href = `/weekly?employeeId=${encodeURIComponent(row.employeeId)}&segmentId=${encodeURIComponent(row.segmentId)}`;
  const hervorheben = row.fehlendeWochen >= LUECKEN_HERVORHEBEN_AB;

  return (
    <Table.Tr style={{ cursor: "pointer" }} onClick={() => onOpen(paar)}>
      <Table.Td>
        <Anchor
          href={href}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            // Strg-/Cmd-/Umschalt-/Alt-Klick bleibt beim Browser (neuer Tab, Fenster, Download);
            // Mittelklick löst kein `click` aus, sondern `auxclick`, und landet gar nicht hier.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            onOpen(paar);
          }}
        >
          {row.fullName}
        </Anchor>
      </Table.Td>
      <Table.Td>{row.segmentName}</Table.Td>
      <Table.Td>
        <LetztesWeekly last={row.lastWeekly} />
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <Badge size="sm" variant="light" color={row.openTodos.lead > 0 ? "blue" : "gray"}>
            {t("weekly.overview.todosLead", { count: row.openTodos.lead })}
          </Badge>
          <Badge size="sm" variant="light" color={row.openTodos.fuehrung > 0 ? "violet" : "gray"}>
            {t("weekly.overview.todosLeadership", { count: row.openTodos.fuehrung })}
          </Badge>
        </Group>
      </Table.Td>
      <Table.Td ta="right">
        <Text
          span
          size="sm"
          fw={hervorheben ? 700 : undefined}
          c={hervorheben ? "red" : undefined}
          data-testid={`missing-weeks-${row.employeeId}-${row.segmentId}`}
        >
          {row.fehlendeWochen}
        </Text>
        {row.luecken.length > 0 && (
          <Text size="xs" c="dimmed">
            {row.luecken.map((w) => kwLabel(t, w)).join(", ")}
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

function LetztesWeekly({ last }: { last: WeeklyOverviewRow["lastWeekly"] }) {
  const { t } = useTranslation();
  if (!last) {
    return (
      <Text size="sm" c="dimmed">
        {t("weekly.overview.noWeeklyYet")}
      </Text>
    );
  }
  const offen = last.status === "offen";
  return (
    <Stack gap={2}>
      <Group gap={6} wrap="nowrap">
        <Text size="sm" style={{ whiteSpace: "nowrap" }}>
          {kwLabel(t, last)}
        </Text>
        <Badge size="sm" variant="light" color={offen ? "blue" : "gray"}>
          {offen ? t("weekly.statusOpen") : t("weekly.statusClosed")}
        </Badge>
      </Group>
      {!offen && last.closedAt && (
        <Text size="xs" c="dimmed">
          {t("weekly.overview.closedOn", { date: formatBerlinDate(last.closedAt) })}
        </Text>
      )}
    </Stack>
  );
}
